import { useState, useEffect, useCallback, useRef } from 'react';
import { structuredInputsAPI } from '../lib/api';
import { useProject } from '../lib/project-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import {
  Plus, Trash2, ChevronDown, ChevronUp, GripVertical,
  DollarSign, Users, BarChart3, Info, RefreshCw, CheckCircle2,
  AlertTriangle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useConfirm } from '../components/ui/confirm-dialog';

const CAPITAL_TYPES = [
  { value: 'equity_common', label: 'Equity común' },
  { value: 'preferred_equity', label: 'Equity preferente' },
  { value: 'subordinated_debt', label: 'Deuda subordinada' },
  { value: 'mezzanine', label: 'Mezzanine' },
];

const CALC_METHODS = [
  { value: 'compound', label: 'Compuesto anual' },
  { value: 'compound_monthly', label: 'Compuesto mensual' },
  { value: 'simple', label: 'Simple' },
];

function NumInput({ value, onChange, prefix = '', suffix = '', className = '', min, step = 1 }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {prefix && <span className="text-xs text-muted-foreground shrink-0">{prefix}</span>}
      <Input
        type="text" inputMode="decimal"
        className="h-9 text-sm font-medium text-right tabular-nums rounded-lg"
        style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
        value={editing ? raw : (value != null ? Number(value).toLocaleString('es-MX') : '')}
        onFocus={() => { setEditing(true); setRaw(value != null ? String(value) : ''); }}
        onBlur={() => { setEditing(false); const n = parseFloat(raw.replace(/,/g, '')); if (!isNaN(n)) onChange(n); }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        onChange={e => setRaw(e.target.value)}
      />
      {suffix && <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>}
    </div>
  );
}

function PctInput({ value, onChange, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const display = value != null ? `${(value * 100).toFixed(1)}` : '';
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Input
        type="text" inputMode="decimal"
        className="h-9 text-sm font-medium text-right tabular-nums rounded-lg"
        style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
        value={editing ? raw : display}
        onFocus={() => { setEditing(true); setRaw(value != null ? String(value * 100) : ''); }}
        onBlur={() => { setEditing(false); const n = parseFloat(raw); if (!isNaN(n)) onChange(n / 100); }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        onChange={e => setRaw(e.target.value)}
      />
      <span className="text-xs text-muted-foreground shrink-0">%</span>
    </div>
  );
}

/* ═══ Investor Row ═══ */
function InvestorRow({ investor, index, onChange, onDelete, onToggleExpand, expanded }) {
  const inv = investor;
  const calls = inv.capital_calls || [];
  const callSum = calls.reduce((s, c) => s + (c.amount || 0), 0);
  const committed = inv.capital_committed || 0;
  const callMismatch = committed > 0 && Math.abs(callSum - committed) > 1;

  const updateField = (field, val) => onChange(index, { ...inv, [field]: val });
  const updateRate = (field, val) => onChange(index, { ...inv, rate_or_pref_return: { ...inv.rate_or_pref_return, [field]: val } });

  const addCall = () => {
    const lastMonth = calls.length > 0 ? Math.max(...calls.map(c => c.month || 0)) + 6 : 1;
    onChange(index, { ...inv, capital_calls: [...calls, { month: lastMonth, amount: 0 }] });
  };
  const updateCall = (ci, field, val) => {
    const updated = [...calls];
    updated[ci] = { ...updated[ci], [field]: val };
    onChange(index, { ...inv, capital_calls: updated });
  };
  const deleteCall = (ci) => {
    onChange(index, { ...inv, capital_calls: calls.filter((_, i) => i !== ci) });
  };

  return (
    <div className="rounded-xl border border-border/50 bg-card" data-testid={`investor-row-${index}`}>
      {/* Main row */}
      <div className="p-4 flex items-center gap-3">
        <button onClick={() => onToggleExpand(index)} className="text-muted-foreground hover:text-foreground">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        <div className="flex-1 grid grid-cols-6 gap-3 items-center">
          <Input className="h-9 text-sm font-medium rounded-lg col-span-1" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
            value={inv.name || ''} onChange={e => updateField('name', e.target.value)} placeholder="Nombre" />

          <Select value={inv.capital_type || 'preferred_equity'} onValueChange={v => updateField('capital_type', v)}>
            <SelectTrigger className="h-9 text-xs rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
            <SelectContent>{CAPITAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>

          <NumInput value={inv.capital_committed} onChange={v => updateField('capital_committed', v)} prefix="$" />

          <PctInput value={inv.rate_or_pref_return?.value} onChange={v => updateRate('value', v)} />

          <Select value={inv.rate_or_pref_return?.calculation || 'compound'} onValueChange={v => updateRate('calculation', v)}>
            <SelectTrigger className="h-9 text-xs rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
            <SelectContent>{CALC_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>

          <PctInput value={inv.participation_on_excess} onChange={v => updateField('participation_on_excess', v)} />
        </div>

        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => onDelete(index)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Column headers */}
      <div className="px-4 pb-1 grid grid-cols-6 gap-3 text-[9px] uppercase tracking-wider font-medium" style={{ color: '#94A3B8', marginLeft: '2.75rem' }}>
        <span>Nombre</span><span>Tipo</span><span>Monto comprometido</span><span>Tasa/Pref</span><span>Cálculo</span><span>Particip. residual</span>
      </div>

      {/* Expanded: capital calls */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 ml-11" style={{ borderTop: '1px solid var(--border-default)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Calendario de llamadas</span>
            {callMismatch && (
              <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--warning-color)' }}>
                <AlertTriangle className="w-3 h-3" /> Llamadas (${callSum.toLocaleString('es-MX')}) ≠ comprometido (${committed.toLocaleString('es-MX')})
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            {calls.map((c, ci) => (
              <div key={ci} className="flex items-center gap-3" data-testid={`call-${index}-${ci}`}>
                <span className="text-xs w-16 shrink-0" style={{ color: '#64748B' }}>Mes</span>
                <NumInput value={c.month} onChange={v => updateCall(ci, 'month', Math.round(v))} className="w-20" />
                <span className="text-xs w-16 shrink-0" style={{ color: '#64748B' }}>Monto</span>
                <NumInput value={c.amount} onChange={v => updateCall(ci, 'amount', v)} prefix="$" className="flex-1" />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deleteCall(ci)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-2 h-7 text-xs gap-1" onClick={addCall}>
            <Plus className="w-3 h-3" /> Agregar llamada
          </Button>
        </div>
      )}
    </div>
  );
}

/* ═══ Waterfall Hurdle Row ═══ */
function HurdleRow({ hurdle, index, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, investors }) {
  const h = hurdle;
  const recipients = investors.map((inv, i) => ({ value: `investor_${i + 1}`, label: inv.name || `Investor ${i + 1}` }));
  recipients.unshift({ value: 'sponsor', label: 'Sponsor' });

  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-card border border-border/50" data-testid={`hurdle-${index}`}>
      <div className="flex flex-col gap-0.5 shrink-0">
        <button disabled={isFirst} onClick={onMoveUp} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
        <button disabled={isLast} onClick={onMoveDown} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
      </div>
      <Badge variant="outline" className="text-[10px] w-6 h-6 flex items-center justify-center rounded-full shrink-0">{index + 1}</Badge>

      <Input className="h-8 text-xs font-medium rounded-lg flex-1" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
        value={h.concept || ''} onChange={e => onChange(index, { ...h, concept: e.target.value })} placeholder="Concepto" />

      <Select value={h.amount_calculation || 'capital_returned'} onValueChange={v => onChange(index, { ...h, amount_calculation: v })}>
        <SelectTrigger className="h-8 text-xs w-[140px] rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="capital_returned">Capital returned</SelectItem>
          <SelectItem value="pref_return">Pref return</SelectItem>
          <SelectItem value="catch_up">Catch-up</SelectItem>
          <SelectItem value="residual_split">Residual split</SelectItem>
        </SelectContent>
      </Select>

      {h.amount_calculation === 'catch_up' && (
        <PctInput value={h.target} onChange={v => onChange(index, { ...h, target: v })} className="w-20" />
      )}

      {h.amount_calculation === 'residual_split' && (
        <div className="flex items-center gap-1 text-[10px]">
          {Object.entries(h.residual_split || {}).map(([k, v]) => (
            <span key={k} className="px-1.5 py-0.5 rounded" style={{ background: '#EEF2FF' }}>{k.replace('investor_', 'Inv ')}: {(v * 100).toFixed(0)}%</span>
          ))}
        </div>
      )}

      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={() => onDelete(index)}>
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  );
}

/* ═══ Simulation Panel ═══ */
function SimulationPanel({ result, loading }) {
  if (loading) return (
    <Card className="rounded-xl border-border/50 shadow-sm"><CardContent className="p-5 flex items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" /> Calculando...
    </CardContent></Card>
  );
  if (!result) return null;

  const { distributable_pool, waterfall, party_metrics, undistributed } = result;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm" data-testid="simulation-panel">
      <CardContent className="p-5">
        <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Simulación de distribución</h4>

        <div className="text-lg font-display font-semibold tabular-nums mb-4">{formatCurrency(distributable_pool)}<span className="text-xs font-normal ml-2" style={{ color: '#94A3B8' }}>Pool distribuible</span></div>

        {/* Waterfall steps */}
        <div className="space-y-1.5 mb-4">
          {(waterfall || []).map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className="text-[9px] w-5 h-5 flex items-center justify-center rounded-full shrink-0">{h.order}</Badge>
              <span className="flex-1 truncate" style={{ color: '#64748B' }}>{h.concept}</span>
              <span className="font-semibold tabular-nums">{formatCurrency(h.amount)}</span>
            </div>
          ))}
          {undistributed > 0.01 && (
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--danger)' }}>
              <AlertTriangle className="w-3.5 h-3.5" /> No distribuido: {formatCurrency(undistributed)}
            </div>
          )}
        </div>

        {/* Party summaries */}
        <div className="space-y-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
          {Object.entries(party_metrics || {}).map(([key, pm]) => (
            <div key={key} className="rounded-lg p-3" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }} data-testid={`sim-party-${key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold">{pm.name || key}</span>
                <span className="text-sm font-display font-semibold tabular-nums">{formatCurrency(pm.total_received)}</span>
              </div>
              <div className="flex items-center gap-3 text-[10px]" style={{ color: '#64748B' }}>
                {pm.capital_committed > 0 && <span>Capital: {formatCurrency(pm.capital_committed)}</span>}
                {pm.pref_return_amount > 0 && <span>Pref: {formatCurrency(pm.pref_return_amount)}</span>}
                {pm.moic && <span className="font-semibold">MOIC {pm.moic}x</span>}
                {pm.irr_annual != null && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="font-semibold flex items-center gap-0.5" style={{ color: '#94A3B8' }}>
                          TIR ~{pm.irr_annual}% <Info className="w-3 h-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        Estimada (lump-sum). Se refinará con distribution_curve en próximo sprint.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Verification */}
        <div className="mt-3 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--success)' }}>
          <CheckCircle2 className="w-3 h-3" />
          Verificación: {formatCurrency(Object.values(party_metrics || {}).reduce((s, p) => s + (p.total_received || 0), 0) + (undistributed || 0))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══ Main CapitalStack Component ═══ */
export default function CapitalStackPage() {
  const { activeProject, activeScenario } = useProject();
  const pid = activeProject?.id;
  const sid = activeScenario?.id;
  const confirm = useConfirm();

  const [sponsor, setSponsor] = useState({ capital_committed: 0, promote_target: 0.25, dev_fee: 0, asset_management_fee_pct_annual: 0 });
  const [investors, setInvestors] = useState([]);
  const [waterfall, setWaterfall] = useState([]);
  const [expandedInv, setExpandedInv] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [autoSim, setAutoSim] = useState(true);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  // Load
  useEffect(() => {
    if (!sid) return;
    // Reset state for the new scenario
    setSponsor({ capital_committed: 0, promote_target: 0.25, dev_fee: 0, asset_management_fee_pct_annual: 0 });
    setInvestors([]);
    setWaterfall([]);
    setSimResult(null);
    setLoading(true);

    structuredInputsAPI.capitalStructure.get(sid).then(r => {
      const d = r.data;
      if (d.sponsor && Object.keys(d.sponsor).length) setSponsor(d.sponsor);
      if (d.investors?.length) setInvestors(d.investors);
      if (d.waterfall?.length) setWaterfall(d.waterfall);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [sid]);

  // Persist with debounce — skip during loading (prevents saving empty state on scenario switch)
  const persist = useCallback(() => {
    if (!sid || loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      structuredInputsAPI.capitalStructure.save(sid, { sponsor, investors, waterfall }).catch(() => {});
    }, 1000);
  }, [sid, sponsor, investors, waterfall, loading]);

  useEffect(() => { if (!loading) persist(); }, [sponsor, investors, waterfall]);

  // Auto-simulate
  const simulate = useCallback(async () => {
    if (!sid || !pid || investors.length === 0) { setSimResult(null); return; }
    setSimLoading(true);
    try {
      const r = await structuredInputsAPI.capitalStructure.simulate(sid, pid, { sponsor, investors, waterfall });
      setSimResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en simulación');
      setSimResult(null);
    }
    setSimLoading(false);
  }, [sid, pid, sponsor, investors, waterfall]);

  useEffect(() => {
    if (autoSim && investors.length > 0) {
      const t = setTimeout(simulate, 1500);
      return () => clearTimeout(t);
    }
  }, [autoSim, sponsor, investors, waterfall, simulate]);

  // Handlers
  const addInvestor = () => {
    setInvestors([...investors, {
      name: `Inversionista ${investors.length + 1}`,
      capital_committed: 0,
      capital_type: 'preferred_equity',
      rate_or_pref_return: { value: 0.10, calculation: 'compound' },
      participation_on_excess: 0.50,
      capital_calls: [],
    }]);
  };

  const updateInvestor = (idx, inv) => { const u = [...investors]; u[idx] = inv; setInvestors(u); };
  const deleteInvestor = async (idx) => {
    const ok = await confirm({
      title: '¿Eliminar aportante?',
      description: 'El aportante y su participación en el waterfall se removerán del capital stack.',
      confirmLabel: 'Eliminar',
      variant: 'destructive',
    });
    if (ok) setInvestors(investors.filter((_, i) => i !== idx));
  };

  const addHurdle = () => {
    setWaterfall([...waterfall, { order: waterfall.length + 1, concept: '', recipients: ['sponsor'], amount_calculation: 'capital_returned', residual_split: {} }]);
  };
  const updateHurdle = (idx, h) => { const u = [...waterfall]; u[idx] = h; setWaterfall(u); };
  const deleteHurdle = (idx) => setWaterfall(waterfall.filter((_, i) => i !== idx).map((h, i) => ({ ...h, order: i + 1 })));
  const moveHurdle = (from, to) => {
    const arr = [...waterfall];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setWaterfall(arr.map((h, i) => ({ ...h, order: i + 1 })));
  };

  // Validations
  const errors = [];
  const warnings = [];
  const isAutofinanced = investors.length === 0;

  investors.forEach((inv, i) => {
    if (!inv.capital_calls?.length) warnings.push(`${inv.name || `Inv ${i+1}`}: sin llamadas de capital`);
    const callSum = (inv.capital_calls || []).reduce((s, c) => s + (c.amount || 0), 0);
    if (inv.capital_committed > 0 && Math.abs(callSum - inv.capital_committed) > 1)
      errors.push(`${inv.name}: llamadas ($${callSum.toLocaleString()}) ≠ comprometido ($${inv.capital_committed.toLocaleString()})`);
  });
  if (waterfall.length < 2 && !isAutofinanced) warnings.push('El waterfall necesita al menos 2 hurdles');
  const residualHurdle = waterfall.find(h => h.amount_calculation === 'residual_split');
  if (residualHurdle && !isAutofinanced) {
    const splitSum = Object.values(residualHurdle.residual_split || {}).reduce((s, v) => s + v, 0);
    if (Math.abs(splitSum - 1) > 0.01) errors.push(`Residual split suma ${(splitSum * 100).toFixed(0)}%, debe ser 100%`);
  }
  if (sponsor.promote_target > 0.50) errors.push('Promote target no puede superar 50%');

  if (!sid) return <div className="p-8 text-center text-muted-foreground">Selecciona un escenario para configurar la estructura de capital.</div>;

  return (
    <div className="flex gap-6 max-w-[1600px] px-6 lg:px-8 py-6" data-testid="capital-stack-page">
      {/* Left: editing */}
      <div className="flex-1 space-y-6 min-w-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-display font-semibold">Estructura de Capital</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Configura aportantes, llamadas de capital y distribución waterfall</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px]" style={{ color: '#94A3B8' }}>Auto-recalcular</span>
            <Switch checked={autoSim} onCheckedChange={setAutoSim} className="scale-75" />
            {!autoSim && <Button size="sm" className="h-7 text-xs gap-1" onClick={simulate}><RefreshCw className="w-3 h-3" /> Recalcular</Button>}
          </div>
        </div>

        {/* Sponsor */}
        <Card className="rounded-xl border-border/50 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Sponsor</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-1"><span className="text-[10px] font-medium" style={{ color: '#64748B' }}>Capital comprometido</span><NumInput value={sponsor.capital_committed} onChange={v => setSponsor({ ...sponsor, capital_committed: v })} prefix="$" /></div>
              <div className="space-y-1"><span className="text-[10px] font-medium" style={{ color: '#64748B' }}>Promote target</span><PctInput value={sponsor.promote_target} onChange={v => setSponsor({ ...sponsor, promote_target: v })} /></div>
              <div className="space-y-1"><span className="text-[10px] font-medium" style={{ color: '#64748B' }}>Dev fee</span><NumInput value={sponsor.dev_fee} onChange={v => setSponsor({ ...sponsor, dev_fee: v })} prefix="$" /></div>
              <div className="space-y-1"><span className="text-[10px] font-medium" style={{ color: '#64748B' }}>Asset mgmt fee anual</span><PctInput value={sponsor.asset_management_fee_pct_annual} onChange={v => setSponsor({ ...sponsor, asset_management_fee_pct_annual: v })} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Investors */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Inversionistas</h3>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addInvestor} data-testid="add-investor-btn">
              <Plus className="w-3 h-3" /> Agregar
            </Button>
          </div>
          {investors.length === 0 ? (
            <div className="text-center py-10 rounded-xl border border-dashed border-border">
              <Users className="w-8 h-8 mx-auto mb-2" style={{ color: '#94A3B8', opacity: 0.3 }} />
              <p className="text-sm text-muted-foreground">Sin inversionistas. Agrega uno para configurar la estructura.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {investors.map((inv, i) => (
                <InvestorRow key={i} investor={inv} index={i} onChange={updateInvestor} onDelete={deleteInvestor}
                  expanded={expandedInv === i} onToggleExpand={(idx) => setExpandedInv(expandedInv === idx ? null : idx)} />
              ))}
            </div>
          )}
        </div>

        {/* Waterfall */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Waterfall de distribución</h3>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addHurdle} data-testid="add-hurdle-btn">
              <Plus className="w-3 h-3" /> Agregar hurdle
            </Button>
          </div>
          <div className="space-y-1.5">
            {waterfall.map((h, i) => (
              <HurdleRow key={i} hurdle={h} index={i} onChange={updateHurdle} onDelete={deleteHurdle}
                onMoveUp={() => moveHurdle(i, i - 1)} onMoveDown={() => moveHurdle(i, i + 1)}
                isFirst={i === 0} isLast={i === waterfall.length - 1} investors={investors} />
            ))}
          </div>
        </div>

        {/* Validations */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="space-y-1.5">
            {errors.map((e, i) => (
              <div key={`e-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {e}
              </div>
            ))}
            {warnings.map((w, i) => (
              <div key={`w-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(245,166,35,0.06)', color: '#92400E' }}>
                <Info className="w-3.5 h-3.5 shrink-0" /> {w}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right: simulation panel (sticky) */}
      <div className="w-[340px] shrink-0">
        <div className="sticky top-4">
          <SimulationPanel result={simResult} loading={simLoading} />
        </div>
      </div>
    </div>
  );
}
